/**
 * ReferralMilestoneCelebration — fires when the current user has just
 * crossed another 5-signup milestone. Confetti burst + LinkedIn share
 * sheet pre-filled with a bracket-win headline.
 *
 * Poll flow:
 *   1. UserPortal mounts → GET /api/referrals/celebration
 *   2. If pending, show this modal with signups_reached / days_added
 *   3. On close or share, POST /api/referrals/celebration/dismiss
 */
import React, { useEffect } from 'react';
import { PartyPopper, Linkedin, Copy, X, Download } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const Confetti = () => {
  const pieces = Array.from({ length: 60 });
  const colours = ['#F5B841', '#1e63c9', '#0b1e3f', '#fff', '#6ee7b7', '#f472b6'];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 1.6;
        const dur = 1.8 + Math.random() * 1.6;
        const size = 6 + Math.random() * 8;
        const rot = Math.random() * 360;
        const bg = colours[i % colours.length];
        return (
          <span
            key={i}
            style={{
              position: 'absolute',
              left: `${left}%`,
              top: '-20px',
              width: `${size}px`,
              height: `${size * 0.4}px`,
              background: bg,
              transform: `rotate(${rot}deg)`,
              animation: `ref-confetti-fall ${dur}s ${delay}s linear forwards`,
              opacity: 0.9,
            }}
          />
        );
      })}
      <style>{`
        @keyframes ref-confetti-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

const ReferralMilestoneCelebration = ({ celebration, onClose, shareLink, API }) => {
  useEffect(() => {
    // Only lock body scroll when a celebration is actually visible.
    // The old always-on effect locked scroll even when celebration
    // was null, which broke scrolling on the whole UserPortal.
    if (!celebration) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [celebration]);

  if (!celebration) return null;

  // Two celebration flavours share this modal:
  //  · kind='credit_milestone' — the 5/10/25 tiered credit reward
  //  · default (bracket-based) — the +30d Pro extension every 5 signups
  const isCredit = celebration.kind === 'credit_milestone';
  const signups   = celebration.signups_reached || 0;
  const days      = celebration.days_added || 30;
  const bracket   = celebration.bracket || 1;
  const credits   = celebration.credits_added || 0;
  const threshold = celebration.threshold || bracket;

  const downloadCard = async () => {
    if (!API || ![5, 10, 25].includes(threshold)) return;
    try {
      const tk = window.localStorage.getItem('token');
      const axios = (await import('axios')).default;
      const res = await axios.get(`${API}/referrals/milestone/${threshold}.png`, {
        responseType: 'blob',
        headers: tk ? { Authorization: `Bearer ${tk}` } : {},
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fda-milestone-${threshold}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error('Could not download share card');
    }
  };

  // Prefer the server-generated LinkedIn URL (against a top cert). Fall back
  // to sharing the user's own referral link if we have it.
  const fallback = shareLink
    ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink)}`
    : null;
  const linkedInUrl = celebration.share_url || fallback;

  const headline = isCredit
    ? `🎉 Just crossed ${signups} referrals and unlocked +${credits} AI credits at Freeze Dry Academy!`
    : bracket === 1
      ? `🎉 5 referrals earned my first free month at Freeze Dry Academy!`
      : `🎉 ${signups} referrals · that's ${bracket} free months of Pro at Freeze Dry Academy!`;

  const copyHeadline = async () => {
    try {
      await navigator.clipboard.writeText(headline);
      toast.success('Headline copied — paste it into your post');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-navy/85 backdrop-blur-sm animate-in fade-in duration-500"
      role="dialog"
      aria-label="Referral milestone celebration"
      data-testid="referral-milestone-celebration"
    >
      <Confetti />
      <div className="relative max-w-lg w-[92vw] pro-card bg-card p-8 sm:p-10 text-center animate-in zoom-in-95 slide-in-from-bottom-4 duration-500 border-2 border-primary/40">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 h-9 w-9 rounded-full hover:bg-primary/10 inline-flex items-center justify-center text-foreground/60 hover:text-foreground transition"
          data-testid="referral-milestone-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-500/15 text-amber-600 flex items-center justify-center mb-4">
          <PartyPopper className="w-7 h-7" />
        </div>
        <div className="eyebrow text-primary">Milestone unlocked</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight mt-2">
          {isCredit ? (
            <>
              <span className="text-amber-brand">{signups}</span> referrals · <br />
              <span className="text-amber-brand">+{credits.toLocaleString()} AI credits</span>
            </>
          ) : (
            <>
              <span className="text-amber-brand">{signups}</span> referrals · <br />
              <span className="text-amber-brand">+{days} days</span> of Pro on us
            </>
          )}
        </h2>
        <p className="mt-4 text-sm text-foreground/75 leading-relaxed">
          {isCredit ? (
            <>You just crossed the <b>{threshold}-signup</b> milestone. The credits are already in your dashboard — spend them on the AI coaches or save them for a rainy launch day.</>
          ) : (
            <>You just crossed another 5-signup bracket. Your Pro access has been extended automatically. Every additional 5 signups extends it by another month — share the win to keep the loop going.</>
          )}
        </p>

        <div className="mt-6 rounded-xl border border-border bg-muted/40 p-3 text-left">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Suggested post</div>
          <p className="mt-1 text-sm text-foreground/90 leading-snug italic" data-testid="referral-milestone-headline">
            "{headline}"
          </p>
          <button
            type="button"
            onClick={copyHeadline}
            className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
            data-testid="referral-milestone-copy-headline"
          >
            <Copy className="w-3 h-3" /> Copy headline
          </button>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          {isCredit && [5, 10, 25].includes(threshold) && (
            <Button
              variant="outline"
              onClick={downloadCard}
              className="rounded-full px-6 py-6 font-bold border-amber-500/60 text-amber-800 dark:text-amber-300 hover:bg-amber-500/10"
              data-testid="referral-milestone-download-card"
            >
              <Download className="w-4 h-4 mr-1.5" /> Download brag card
            </Button>
          )}
          {linkedInUrl && (
            <a
              href={linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-1.5 rounded-full h-12 px-6 text-sm font-bold bg-[#0A66C2] text-white hover:bg-[#0a5aad] transition-colors"
              data-testid="referral-milestone-share-linkedin"
            >
              <Linkedin className="w-4 h-4" /> Share the win
            </a>
          )}
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-full px-6 py-6 font-bold"
            data-testid="referral-milestone-dismiss"
          >
            Keep going
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReferralMilestoneCelebration;
